import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useTwinData } from '@hooks/storeHooks/useTwin';
import {
  useApproveMentor,
  useDeleteTwin,
  useTwinPersonaById,
} from '../../../../hooks/apiHooks/twinHooks';
import { FailedState } from './FailedState';
import { GeneratingState } from './GeneratingState';
import { PublishedSuccessState } from './PublishedSuccessState';
import { WaitingForApprovalState } from './WaitingForApprovalState';

interface PublishTabProps {
  twinId: string | null;
  locationSearch: string | null;
  refetchUser: () => void;
}

const PublishTab: React.FC<PublishTabProps> = memo(
  ({ twinId, refetchUser }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [isPublished, setIsPublished] = useState(false);

    const { setLocalTwinIsReady, resetTwinState } = useTwinData();

    const { data, isLoading } = useTwinPersonaById(twinId || '', true, true);
    const { mutateAsync: deleteTwin, isPending: isDeleting } = useDeleteTwin();
    const { mutateAsync: approveMentor, isPending: isApproving } =
      useApproveMentor();

    const [searchParams] = useSearchParams();
    const isMacos = searchParams.get('platform') === 'macos';

    const mentorStatus = useMemo(
      () => data?.data?.profile?.profile?.status,
      [data],
    );

    const personaData = useMemo(() => data?.data, [data]);

    const companion = {
      mentorId: twinId || '',
      avatarId:
        (personaData?.profile?.profile?.avatar?.versions?.includes('v1')
          ? personaData?.profile?.profile?.avatar?.id
          : personaData?.profile?.profile?.avatarId) || '',
      slug: personaData?.profile?.profile?.slug || '',
    };

    // Only show avatar when we have all required data
    const shouldShowAvatar = useMemo(() => {
      return (
        (mentorStatus === 'ReadyToUse' ||
          mentorStatus === 'WaitingForApproval') &&
        companion.avatarId &&
        companion.mentorId &&
        companion.slug
      );
    }, [mentorStatus, companion]);

    useEffect(() => {
      if (
        (mentorStatus === 'ReadyToUse' ||
          mentorStatus === 'WaitingForApproval') &&
        twinId
      ) {
        refetchUser();
      }
    }, [mentorStatus, twinId, refetchUser]);

    const handleRestart = async () => {
      await deleteTwin();
      resetTwinState();
      window.location.href = `/twin${location.search}`;
    };

    const handlePublish = async () => {
      if (!twinId) {
        toast.error(t('twin.noTwinId'));
        return;
      }

      try {
        // call the approve API
        const response = await approveMentor(twinId);

        // check if response success
        if (!response?.success || !response?.approveMentor?.success) {
          console.error('Approve mentor failed:', response);
          toast.error(t('twin.publishFailed'));
          return;
        }
        toast.success(t('twin.publishSuccess'));
        resetTwinState();
        setLocalTwinIsReady(true);
        if (isMacos) {
          try {
            // Call the deep link for isMacOS/isIOS with mentor ID
            window.location.href = `napster-companion://cloning-success/${twinId}`;
            window.parent.postMessage(
              { type: 'cloning-success', mentorId: twinId },
              '*',
            );
          } catch (error) {
            console.error('Failed to open deep link:', error);
          }
        }

        // Show the success screen
        setIsPublished(true);
      } catch (error) {
        console.error('Error approving mentor:', error);
        toast.error(t('twin.publishFailed'));
      }
    };

    const renderCurrentState = () => {
      switch (mentorStatus) {
        case 'WaitingForApproval':
          if (isPublished) {
            return (
              <PublishedSuccessState
                twinId={twinId || ''}
                personaSlug={personaData?.profile?.profile?.slug || ''}
                isMacos={isMacos}
              />
            );
          }

          return (
            <WaitingForApprovalState
              shouldShowAvatar={!!shouldShowAvatar}
              isLoading={isLoading}
              companion={companion}
              onPublish={handlePublish}
              onRestart={handleRestart}
              isApproving={isApproving}
              isDeleting={isDeleting}
            />
          );

        case 'Failed':
          return (
            <FailedState onRestart={handleRestart} isDeleting={isDeleting} />
          );

        default:
          return (
            <GeneratingState
              profilePictureUrl={
                personaData?.profile?.profile?.compressedProfilePictureUrl
              }
            />
          );
      }
    };

    return <>{renderCurrentState()}</>;
  },
);

export { PublishTab };
